import React, { useState } from "react";
import { Modal, Button, Badge, Image } from "react-bootstrap";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaUserEdit } from "react-icons/fa";
import { MdMail } from "react-icons/md";
import { isLargeContent } from "../Constant/constant";

export interface HistoryRecord {
  updatedBy: string;
  updatedByName: string;
  field: string;
  oldValue: string;
  newValue: string;
  updatedAt: string;
}
interface PopProps {
  show: boolean;
  handleClose: () => void;
  historyData: HistoryRecord[];
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} ${month} ${year}, ${time}`;
};

const renderBadges = (value: string, variant: string) => {
  if (!value.trim())
    return (
      <Badge bg="light" text="dark">
        N/A
      </Badge>
    );
  return value
    .replace(/"/g, "")
    .split(",")
    .map((val, idx) => (
      <Badge key={idx} bg={variant} className="me-1 text-white">
        {val.trim()}
      </Badge>
    ));
};

const Pop: React.FC<PopProps> = ({ show, handleClose, historyData }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const filteredHistory = historyData
    .filter(
      (record) =>
        record.field?.trim() !== "" &&
        record.oldValue?.trim() !== record.newValue?.trim()
    )
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton className="bg-white border-b">
        <div className="flex flex-col w-full">
          <Modal.Title className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Image
              src={require("../../assets/history.png")}
              className="w-7 ms-2"
              alt="History Icon"
            />
            View History
          </Modal.Title>
          <p className="text-sm text-gray-500 mt-1 ms-3">
            View all changes made to this record over time
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="bg-gray-50 p-6 rounded-lg shadow-inner overflow-y-auto" style={{ maxHeight: "60vh" }}>
        {filteredHistory.length > 0 ? (
          <div className="space-y-4">
            {filteredHistory.map((record, index) => {
              const isVertical = isLargeContent(record);
              const isExpanded = expandedIndex === index;
              return (
                <div
                  key={index}
                  className="p-4 bg-white border border-gray-200 rounded-lg shadow-md transition-all duration-300 ease-in-out hover:scale-[1.02]"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-gray-800 font-semibold">
                      <FaUserEdit className="text-blue-600" size={24} />
                      <span>{record.updatedByName}</span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-800 font-semibold">
                      <MdMail className="text-blue-600" size={24} />
                      <span>{record.updatedBy}</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 text-sm">
                        {formatDate(record.updatedAt)}
                      </span>
                      <Button
                        variant="primary"
                        className="p-2 rounded-full transition-all duration-300"
                        onClick={() => toggleExpand(index)}
                      >
                        {isExpanded ? (
                          <IoIosArrowUp size={20} />
                        ) : (
                          <IoIosArrowDown size={20} />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div
                    className={`transition-all duration-500 ease-in-out overflow-hidden ${
                      isExpanded ? "max-h-[500px] opacity-100 mt-4" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div
                      className={`flex ${
                        isVertical ? "flex-col space-y-2" : "flex-row flex-wrap gap-x-8"
                      }`}
                    >
                      <div className="flex items-start gap-2 min-w-[200px]">
                        <strong className="text-gray-800">Field:</strong>
                        <span className="font-medium break-words">
                          {record.field}
                        </span>
                      </div>

                      <div className="flex items-start gap-2 min-w-[200px]">
                        <strong className="text-gray-800">Previous Value:</strong>
                        <div className="flex flex-wrap gap-1">
                          {renderBadges(record.oldValue, "secondary")}
                        </div>
                      </div>

                      <div className="flex items-start gap-2 min-w-[200px]">
                        <strong className="text-gray-800">Updated Value:</strong>
                        <div className="flex flex-wrap gap-1">
                          {renderBadges(record.newValue, "success")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-600">No edit history available.</p>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-white border-t">
        <Button
          variant="secondary"
          onClick={handleClose}
          className="px-4 py-2 rounded-md transition-colors hover:bg-gray-600 mr-7"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Pop;
